import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const LABELS = {
  reply: { title: 'Inbound Replies', action: 'Reply' },
  conversation_started: { title: 'Convos Started', action: 'Start convo' },
};

function normalizeSearch(value) {
  return String(value || '').trim().toLowerCase();
}

export default function TodayInboundActivitySearchModal({ open, mode = 'reply', onClose, onRecorded, onCreateLeadRequested }) {
  const { workspace } = useAuth();
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingLeadId, setSavingLeadId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setError('');
    setSavingLeadId(null);
    let cancelled = false;
    setLoading(true);
    supabase
      .from('leads')
      .select('id,name,instagram_handle,motion,current_inbound_list_id')
      .eq('workspace_id', workspace.id)
      .is('deleted_at', null)
      .eq('validity', 'valid')
      .neq('archive_state', 'archived')
      .order('lead_entered_at', { ascending: false })
      .then(({ data, error: leadError }) => {
        if (cancelled) return;
        setLoading(false);
        if (leadError) {
          setLeads([]);
          setError(leadError.message || 'Leads could not be loaded.');
          return;
        }
        setLeads(data ?? []);
      });
    return () => { cancelled = true; };
  }, [open, workspace.id, mode]);

  const filteredLeads = useMemo(() => {
    const q = normalizeSearch(search);
    if (!q) return leads;
    return leads.filter(lead => `${lead.name} ${lead.instagram_handle ?? ''}`.toLowerCase().includes(q));
  }, [leads, search]);

  if (!open) return null;

  const copy = LABELS[mode] ?? LABELS.reply;
  const hasSearch = Boolean(search.trim());
  const canAddLead = hasSearch && !loading && filteredLeads.length === 0;

  async function record(lead) {
    if (savingLeadId || lead.motion === 'outbound') return;
    setSavingLeadId(lead.id);
    setError('');
    const { data, error: recordError } = await supabase.rpc('record_inbound_today_activity', {
      p_workspace_id: workspace.id,
      p_lead_id: lead.id,
      p_kind: mode,
    });
    setSavingLeadId(null);
    if (recordError) {
      setError(recordError.message || 'Activity could not be recorded.');
      return;
    }
    onRecorded?.({ lead, mode, created: false, result: data?.[0] ?? null });
    onClose?.();
  }

  function createLead() {
    if (!canAddLead) return;
    onCreateLeadRequested?.({ initialSearch: search.trim(), mode });
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose?.()}>
      <div className="modal-card inbound-list-modal" role="dialog" aria-modal="true" aria-labelledby="todayInboundActivityTitle">
        <div className="modal-head">
          <div>
            <h2 id="todayInboundActivityTitle">{copy.title}</h2>
            <p>Search for the lead first. Add them only if they do not already exist.</p>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body inbound-list-body">
          <div className="inbound-list-toolbar">
            <div className="inbound-search-control">
              <div className={`inbound-search-input-shell${canAddLead ? ' has-add-action' : ''}`}>
                <input
                  className="text-input inbound-list-search"
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' && canAddLead) {
                      event.preventDefault();
                      createLead();
                    }
                  }}
                  placeholder="Search leads…"
                  autoFocus
                />
                {canAddLead && <button type="button" className="primary-button no-margin compact inbound-search-add-button" onClick={createLead}>+ Add Lead</button>}
              </div>
            </div>
          </div>

          <div className="inbound-selected-list-head">
            <div><strong>Leads</strong><span>{hasSearch ? `${filteredLeads.length} match${filteredLeads.length === 1 ? '' : 'es'}` : `${filteredLeads.length} workspace lead${filteredLeads.length === 1 ? '' : 's'}`}</span></div>
          </div>

          <div className="inbound-list-results inbound-lead-results">
            {loading ? <div className="compact-empty">Loading leads…</div> : filteredLeads.length === 0 ? (
              <div className="compact-empty">
                {hasSearch ? <><strong>No matching leads found</strong><span>Add this person as a new inbound lead to continue.</span></> : <strong>No leads in this workspace yet</strong>}
              </div>
            ) : filteredLeads.map(lead => (
              <button type="button" className="inbound-list-option inbound-lead-option" key={lead.id} onClick={() => record(lead)} disabled={Boolean(savingLeadId) || lead.motion === 'outbound'}>
                <span><strong>{lead.name}</strong><small>{lead.instagram_handle || 'No Instagram handle'} · {lead.motion === 'inbound' ? 'Inbound' : 'Outbound'}</small></span>
                <span>{lead.motion === 'outbound' ? 'Outbound' : savingLeadId === lead.id ? 'Saving…' : copy.action}</span>
              </button>
            ))}
          </div>

          {canAddLead && <div className="small-note">The guided Create Lead flow will open with {mode === 'conversation_started' ? 'Reply and Conversation started' : 'Reply'} preselected. You can edit those selections before saving.</div>}
          {error && <div className="error-message">{error}</div>}
        </div>
      </div>
    </div>
  );
}
